import { ReactNode, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (item: T) => ReactNode;
  sortable?: boolean;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  isLoading?: boolean;
  emptyMessage?: string;
  // Pagination
  page?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  // Sorting
  sortConfig?: SortConfig;
  onSort?: (config: SortConfig) => void;
  // Selection
  selectable?: boolean;
  selectedIds?: Set<number | string>;
  onSelectionChange?: (ids: Set<number | string>) => void;
  // Row click
  onRowClick?: (item: T) => void;
  // Mobile
  mobileBreakpoint?: number;
  mobileRender?: (item: T) => ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  keyField,
  isLoading = false,
  emptyMessage = 'Nenhum registro encontrado',
  page = 1,
  pageSize = 50, // Padrão 50 itens por página conforme NotaGoiana
  totalItems,
  onPageChange,
  sortConfig,
  onSort,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  onRowClick,
  mobileBreakpoint = 768,
  mobileRender,
}: DataTableProps<T>) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= mobileBreakpoint);
  const totalPages = totalItems ? Math.ceil(totalItems / pageSize) : 1;
  const showPagination = totalItems !== undefined && totalItems > pageSize;

  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      const allIds = new Set(data.map((item) => item[keyField] as number | string));
      onSelectionChange(allIds);
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectRow = (id: number | string, checked: boolean) => {
    if (!onSelectionChange) return;
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    onSelectionChange(newSelected);
  };

  const handleSort = (key: string) => {
    if (!onSort) return;
    const column = columns.find(c => c.key === key);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';
    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') newDirection = 'desc';
      else if (sortConfig.direction === 'desc') newDirection = null;
    }

    onSort({ key, direction: newDirection });
  };

  const renderSortIcon = (key: string, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig?.key !== key || sortConfig.direction === null) {
      return <ArrowUpDown className="w-4 h-4 text-slate-300" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-4 h-4 text-primary-600" />
      : <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  const allSelected = data.length > 0 && data.every((item) => selectedIds.has(item[keyField] as number | string));
  const someSelected = data.some((item) => selectedIds.has(item[keyField] as number | string));

  // Renderização mobile como cards
  if (isMobile && mobileRender) {
    return (
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Carregando...</span>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-white rounded-2xl border border-slate-200/70">
            {emptyMessage}
          </div>
        ) : (
          <>
            {data.map((item) => {
              const id = item[keyField] as number | string;
              const isSelected = selectedIds.has(id);
              return (
                <div
                  key={id}
                  onClick={() => onRowClick?.(item)}
                  className={`
                    bg-white rounded-2xl border border-slate-200/70 p-4 transition-colors
                    ${onRowClick ? 'cursor-pointer active:bg-slate-50' : ''}
                    ${isSelected ? 'border-primary-400 bg-primary-50/50' : ''}
                  `}
                >
                  {selectable && (
                    <div className="flex items-center gap-3 mb-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(id, e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-xs text-slate-400">Selecionar</span>
                    </div>
                  )}
                  {mobileRender(item)}
                </div>
              );
            })}
          </>
        )}

        {/* Pagination Mobile */}
        {showPagination && (
          <div className="flex items-center justify-between py-3">
            <div className="text-sm text-slate-500">
              {page} de {totalPages}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange?.(page - 1)}
                disabled={page === 1}
                className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => onPageChange?.(page + 1)}
                disabled={page === totalPages}
                className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Renderização desktop como tabela
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50/70 border-b border-slate-100">
            <tr>
              {selectable && (
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider ${
                    col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'
                  } ${col.sortable ? 'cursor-pointer hover:bg-slate-100 select-none' : ''}`}
                  style={{ width: col.width }}
                >
                  <div className={`flex items-center gap-1 ${
                    col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : ''
                  }`}>
                    <span>{col.header}</span>
                    {renderSortIcon(col.key, col.sortable)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  <div className="flex items-center justify-center gap-2 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Carregando...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center text-slate-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const id = item[keyField] as number | string;
                const isSelected = selectedIds.has(id);
                return (
                  <tr
                    key={id}
                    onClick={() => onRowClick?.(item)}
                    className={`
                      transition-colors
                      ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50'}
                      ${isSelected ? 'bg-primary-50/50' : ''}
                    `}
                  >
                    {selectable && (
                      <td className="w-12 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectRow(id, e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-sm text-slate-600 ${
                          col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {col.render
                          ? col.render(item)
                          : (item[col.key] as ReactNode) ?? '-'}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
          <div className="text-sm text-slate-500">
            Mostrando {Math.min((page - 1) * pageSize + 1, totalItems!)} a{' '}
            {Math.min(page * pageSize, totalItems!)} de {totalItems} registros
          </div>
          <div className="flex items-center gap-1 text-slate-500">
            <button
              onClick={() => onPageChange?.(1)}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Primeira página"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange?.(page - 1)}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Página anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 text-sm font-medium text-slate-700">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => onPageChange?.(page + 1)}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Próxima página"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange?.(totalPages)}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Última página"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
